import { getKeys } from '@morph-mapper/utils';
import { makeRegistry } from './utils/types';
import type { PluginHooks } from './types';
import { anchorExtract } from './definitions/anchor-extract';
import { useTableLogic } from './definitions/declare-items';
import { useIterator } from './definitions/iterators';
import { sortSuggestions } from './definitions/sort-suggestions';
import { Selector } from './definitions/ai-selector';
import { Checking } from './definitions/ai-checking';
import { numberHandling } from './definitions/number-handling';
import { splitHandling } from './definitions/split-handling';
import { dateHandling } from './definitions/date-handling';
import { textCleanup } from './definitions/text-cleanup';
import { compose } from './definitions/compose';
import { removeWords } from './definitions/remove-words';
import { substring } from './definitions/substring';
/**
 * Enum of available plugins.
 */
export enum Plugin {
  AnchorExtract = 'anchorExtract',
  DeclareItems = 'declareItems',
  Iterator = 'iterator',
  sortSuggestions = 'sortSuggestions',
  AiSelector = 'Selector',
  AiChecking = 'Checking',
  NumberHandling = 'numberHandling',
  SplitHandling = 'splitHandling',
  DateHandling = 'dateHandling',
  TextCleanup = 'textCleanup',
  Compose = 'compose',
  RemoveWords = 'removeWords',
  Substring = 'substring',
}

/**
 * Registry of available plugins.
 */
export const registry = makeRegistry({
  [Plugin.AnchorExtract]: anchorExtract,
  [Plugin.DeclareItems]: useTableLogic,
  [Plugin.Iterator]: useIterator,
  [Plugin.sortSuggestions]: sortSuggestions,
  [Plugin.AiSelector]: Selector,
  [Plugin.AiChecking]: Checking,
  [Plugin.NumberHandling]: numberHandling,
  [Plugin.SplitHandling]: splitHandling,
  [Plugin.DateHandling]: dateHandling,
  [Plugin.TextCleanup]: textCleanup,
  [Plugin.Compose]: compose,
  [Plugin.RemoveWords]: removeWords,
  [Plugin.Substring]: substring,
} as const);

/**
 * List of available plugins.
 */
export const availablePlugins = getKeys(registry);

/**
 * Whether a plugin key from saved data is still registered. Templates can
 * list plugins that were removed after they were saved.
 */
export const isRegisteredPlugin = (key: unknown): key is Plugin =>
  availablePlugins.includes(key as Plugin);

/**
 * The name a plugin shows in the builder ('Parse Number' for numberHandling),
 * or the key itself for a plugin that doesn't declare one.
 */
export const pluginDisplayName = (key: string): string => {
  const hook = isRegisteredPlugin(key) ? registry[key] : undefined;
  const name = hook && 'pluginName' in hook ? hook.pluginName : undefined;
  return typeof name === 'string' && name !== '' ? name : key;
};
